export type TAsyncTuple<ErrorType = unknown, DataType = unknown> =
  | {
      error: ErrorType;
      data: null;
    }
  | { error: null; data: DataType };

/**
 * Gracefully handles a given Promise factory.
 * @example
 * const { error, data } = await handleAsync(() => asyncAction())
 */
export const handleAsync = async <ErrorType = unknown, DataType = unknown>(
  promise: () => Promise<DataType>
): Promise<TAsyncTuple<ErrorType, DataType>> => {
  try {
    const data = await promise();
    return { error: null, data };
  } catch (error) {
    // Cancelled requests (fetch AbortError, axios CanceledError/ERR_CANCELED) are
    // expected during refetches/unmounts — don't log them as errors.
    const name = error instanceof Error ? error.name : "";
    const code = (error as { code?: string } | null)?.code;
    const isCancellation =
      name === "AbortError" || name === "CanceledError" || code === "ERR_CANCELED";
    if (import.meta.env.DEV && !isCancellation) {
      console.error(
        "[async] Promise execution failed",
        error instanceof Error ? `${error.name}: ${error.message}` : error
      );
    }
    return { error: error as ErrorType, data: null };
  }
};
